import { useTranslation } from 'react-i18next';
import { PieChart } from '@/shared/components/charts/PieChart';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useTaskDistribution } from '../../hooks/useTaskDistribution';
import type { ChartDataPoint } from '@/shared/components/charts/types';

const STATUS_COLORS: Record<string, string> = {
  Todo: '#94a3b8',
  InProgress: '#06b6d4',
  Done: '#3b82f6',
};

const PRIORITY_COLORS: Record<string, string> = {
  None: '#94a3b8',
  Low: '#94a3b8',
  Medium: '#06b6d4',
  High: '#8b5cf6',
  Urgent: '#ef4444',
};

const DEFAULT_COLORS = ['#6366f1', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];

function ChartLegend({ items }: { items: ChartDataPoint[] }) {
  return (
    <div className="mt-3 space-y-1.5">
      {items.map((item, i) => (
        <div key={item.name} className="flex items-center gap-2 text-xs">
          <span className="w-5 shrink-0 text-right font-medium text-foreground">{item.value}</span>
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: item.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length] }}
          />
          <span className="min-w-0 flex-1 truncate text-muted-foreground">{item.name}</span>
        </div>
      ))}
    </div>
  );
}

export function TaskDistributionSection() {
  const { t } = useTranslation('analytics');
  const { data, isLoading } = useTaskDistribution();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-64 w-full" />
        ))}
      </div>
    );
  }

  const byStatus: ChartDataPoint[] = (data?.byStatus ?? []).map((item) => ({
    name: t(`statusType.${item.label ?? 'Todo'}`),
    value: item.count,
    color: STATUS_COLORS[item.label ?? ''],
  }));

  const byPriority: ChartDataPoint[] = (data?.byPriority ?? []).map((item) => ({
    name: t(`priority.${(item.label ?? 'None').toLowerCase()}`),
    value: item.count,
    color: PRIORITY_COLORS[item.label ?? ''],
  }));

  const byProject: ChartDataPoint[] = (data?.byProject ?? []).map((item) => ({
    name: item.projectName ?? '—',
    value: item.count,
    color: item.projectColor ?? undefined,
  }));

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      <div data-testid="analytics-distribution-by-status">
        <p className="mb-2 text-center text-sm font-semibold">{t('distribution.byStatus')}</p>
        <div className="h-52">
          <PieChart data={byStatus} hideLegend showLabel />
        </div>
        <ChartLegend items={byStatus} />
      </div>
      <div data-testid="analytics-distribution-by-priority">
        <p className="mb-2 text-center text-sm font-semibold">{t('distribution.byPriority')}</p>
        <div className="h-52">
          <PieChart data={byPriority} hideLegend showLabel />
        </div>
        <ChartLegend items={byPriority} />
      </div>
      <div data-testid="analytics-distribution-by-project">
        <p className="mb-2 text-center text-sm font-semibold">{t('distribution.byProject')}</p>
        <div className="h-52">
          <PieChart data={byProject} hideLegend showLabel />
        </div>
        <ChartLegend items={byProject} />
      </div>
    </div>
  );
}
